import { Link } from "react-router-dom";
import { useState } from "react";
import {
  CheckCircle2,
  ArrowRight,
  Megaphone,
  ChevronDown,
  Wallet,
  Users,
} from "lucide-react";
import Button from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { useAuth } from "../context/AuthContext";

const PLANS = [
  {
    name: "Free",
    price: "0 RWF",
    period: "/ month",
    features: [
      "Business profile",
      "Marketplace listing",
      "Basic booking",
      "Basic customer management",
      "Payment integration",
      "2% on bookings paid at the store",
      "1 staff member",
    ],
  },
  {
    name: "Pro",
    price: "12,000 RWF",
    period: "/ month · or 3,500 RWF / week",
    highlighted: true,
    features: [
      "Everything in Free",
      "0% on bookings paid at the store",
      "More advanced management",
      "Analytics",
      "Customer history",
      "Promotions",
      "6 staff included",
    ],
  },
];

const FAQS = [
  {
    q: "Is there a free trial?",
    a: "You don't need one — the Free plan lets you get started at no cost. Upgrade to Pro whenever you're ready for more advanced tools.",
  },
  {
    q: "What booking fees does SHUWA charge?",
    a: "When a customer pays online, a 4.5% SHUWA booking fee is added on top of the service price and paid by the customer — for example, a 10,000 RWF service becomes 10,450 RWF. When a customer books and pays at your store, they pay the service price only: Free shops pay SHUWA 2% of it, Pro shops pay 0%. Boost, our optional new-client acquisition tool, carries its own separate commission that you only pay when it brings you a paying customer.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Pro is paid by the month or by the week, with no long-term contract — downgrade to Free or cancel anytime from your dashboard.",
  },
  {
    q: "How does pricing work for extra staff members?",
    a: "The Free plan includes 1 staff member and Pro includes 6. Need more? Add extra staff for 250 RWF/month each.",
  },
  {
    q: "What payment methods can I accept from clients?",
    a: "MTN Mobile Money and Airtel Money payments and deposits are supported through SHUWA Payments, with processing fees that vary by payment method.",
  },
];

export default function PriceList() {
  const { user } = useAuth();
  const [openFaq, setOpenFaq] = useState(0);

  const getStartedTo = !user
    ? "/register?role=business_owner&type=business&next=/business/onboarding"
    : user.role === "business_owner"
    ? "/business"
    : "/business/onboarding?type=business";

  // Pro goes through the business log in, then straight to paying for Pro.
  const getProTo = !user
    ? "/login?next=/business/subscription"
    : user.role === "business_owner"
    ? "/business/subscription"
    : "/business/onboarding?type=business&plan=pro";

  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="relative bg-slate-900 text-white">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-primary/25 blur-[120px]"
        />
        <div className="relative mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 sm:py-24">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-primary-foreground/90 backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            Simple, transparent pricing
          </span>
          <h1 className="mt-5 font-montserrat text-3xl font-bold leading-tight sm:text-5xl">
            Simple pricing, built to grow with you.
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-base text-slate-300 sm:text-lg">
            Start for free, upgrade to Pro when you're ready to grow. Online payments carry a 4.5%
            SHUWA booking fee, added on top of the booking price and paid by the customer.
          </p>
        </div>
      </section>

      {/* Pricing card */}
      <section className="mx-auto -mt-10 max-w-3xl px-4 pb-14 sm:px-6 sm:pb-24">
        <p className="text-center text-xs font-semibold uppercase tracking-wide text-primary">SHUWA pricing</p>
        <div className="mt-4 grid gap-6 sm:grid-cols-2">
          {PLANS.map((plan) => (
            <Card
              key={plan.name}
              className={`relative overflow-hidden p-6 sm:p-8 ${
                plan.highlighted ? "border-primary/20 shadow-xl" : ""
              }`}
            >
              {plan.highlighted && (
                <div
                  aria-hidden
                  className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary/10 blur-3xl"
                />
              )}
              <div className="relative flex flex-col items-center text-center">
                <p className="text-sm font-semibold uppercase tracking-wide text-primary">{plan.name}</p>
                <div className="mt-3 flex items-end justify-center gap-1">
                  <span className="font-montserrat text-3xl font-bold text-slate-900 sm:text-4xl">
                    {plan.price}
                  </span>
                  <span className="pb-1 text-sm text-muted-foreground">{plan.period}</span>
                </div>

                <Button
                  as={Link}
                  to={plan.name === "Pro" ? getProTo : getStartedTo}
                  size="lg"
                  variant={plan.highlighted ? "primary" : "outline"}
                  className={`mt-6 w-full ${plan.highlighted ? "shadow-lg shadow-primary/30" : ""}`}
                >
                  Get started
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>

              <ul className="relative mt-8 space-y-3 border-t border-border pt-6">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span className="text-sm text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>

        {/* Additional staff */}
        <Card className="mt-6 flex flex-col items-start gap-4 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
          <div className="flex items-center gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-montserrat text-base font-bold text-slate-900">Additional staff</h3>
              <p className="mt-1 text-sm text-muted-foreground">250 RWF / month per extra staff member.</p>
            </div>
          </div>
        </Card>

        {/* Commission example */}
        <Card className="mt-6 p-6 sm:p-8">
          <h3 className="font-montserrat text-base font-bold text-slate-900">How SHUWA booking fees work</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Online payment: a 4.5% SHUWA booking fee is added on top of the service price and paid by your
            customer. Book &amp; pay at store: 2% on Free, 0% on Pro, billed on your monthly statement.
          </p>
          <div className="mt-5 grid gap-3 rounded-xl border border-border bg-muted/30 p-4 sm:grid-cols-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Service</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">Hair braid — 10,000 RWF</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">SHUWA booking fee (4.5%)</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">450 RWF</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Customer pays</p>
              <p className="mt-1 text-sm font-semibold text-primary">10,450 RWF</p>
            </div>
          </div>
        </Card>

        {/* Boost add-on */}
        <Card className="mt-6 flex flex-col items-start gap-4 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
          <div className="flex items-center gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Megaphone className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-montserrat text-base font-bold text-slate-900">Boost (optional)</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Free to activate. Get new clients from the SHUWA marketplace and only pay a
                20% commission on their first completed visit (max 10,000 RWF).
              </p>
            </div>
          </div>
        </Card>

        {/* Accepted payment methods */}
        <Card className="mt-6 flex flex-col items-start gap-4 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
          <div className="flex items-center gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Wallet className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-montserrat text-base font-bold text-slate-900">Accepted payment methods</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Pay with MTN Mobile Money, Airtel Money or cash (reconciled daily).
              </p>
            </div>
          </div>
        </Card>
      </section>

      {/* FAQ */}
      <section className="bg-muted/40">
        <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6 sm:py-24">
          <div className="text-center">
            <span className="text-xs font-semibold uppercase tracking-wide text-primary">FAQ</span>
            <h2 className="mt-2 font-montserrat text-2xl font-bold text-slate-900 sm:text-3xl">
              Common pricing questions
            </h2>
          </div>

          <div className="mt-10 space-y-3">
            {FAQS.map((item, i) => {
              const isOpen = openFaq === i;
              return (
                <Card key={item.q} className="overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                    aria-expanded={isOpen}
                  >
                    <span className="text-sm font-semibold text-slate-900 sm:text-base">{item.q}</span>
                    <ChevronDown
                      className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <p className="px-5 pb-4 text-sm text-muted-foreground">{item.a}</p>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-slate-900">
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-32 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-primary/30 blur-[110px]"
        />
        <div className="relative mx-auto flex max-w-6xl flex-col items-center gap-4 px-4 py-16 text-center sm:px-6 sm:py-24">
          <h2 className="font-montserrat text-2xl font-bold text-white sm:text-3xl">
            Ready to get started?
          </h2>
          <p className="max-w-md text-sm text-slate-300">
            Set up your business profile in minutes — no credit card required.
          </p>
          <div className="mt-2 flex flex-wrap justify-center gap-3">
            <Button as={Link} to={getStartedTo} size="lg" className="shadow-lg shadow-primary/30">
              Try it for free
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
